import { BarChart3 } from "lucide-react";
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { tagMeta } from "../lib/taxonomy";
import type { TopTag } from "../lib/api";

const COLOR_BY_POLARITY: Record<string, string> = {
  positive: "#10b981",
  negative: "#f43f5e",
  neutral:  "#f59e0b",
};

export function TopTagsChart({ tags }: { tags: TopTag[] }) {
  const data = tags.map((t) => {
    const m = tagMeta(t.tag_id);
    return {
      label: m.label,
      tag_id: t.tag_id,
      count: t.total_count,
      polarity: m.polarity,
      conf: t.avg_confidence,
    };
  });

  return (
    <section className="rounded-lg border border-zinc-800 bg-zinc-900/40">
      <header className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-brand-400" />
        <h2 className="text-sm font-semibold">Top drivers (last 24h)</h2>
        <span className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500">
          ranked by aggregate count
        </span>
      </header>

      {data.length === 0 ? (
        <div className="p-10 text-center text-zinc-500 text-sm">
          No aggregates released yet for this tenant.
        </div>
      ) : (
        <div className="p-3 h-[420px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
              barCategoryGap={6}
            >
              <XAxis type="number" stroke="#71717a" fontSize={11} />
              <YAxis
                type="category"
                dataKey="label"
                stroke="#a1a1aa"
                fontSize={12}
                width={120}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#18181b",
                  border: "1px solid #27272a",
                  borderRadius: 6,
                  fontSize: 12,
                }}
                labelStyle={{ color: "#fafafa" }}
                cursor={{ fill: "rgba(255,255,255,0.04)" }}
                formatter={(value, _name, item) => {
                  const p = (item as { payload?: { conf?: number } } | undefined)?.payload;
                  const conf = typeof p?.conf === "number" ? p.conf.toFixed(2) : "—";
                  return [`${value} (conf ${conf})`, "count"];
                }}
              />
              <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                {data.map((d, i) => (
                  <Cell key={i} fill={COLOR_BY_POLARITY[d.polarity] ?? "#52525b"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}
